import { uniqBy } from "lodash";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Bar, BarChart, Cell, Tooltip, XAxis, YAxis } from "recharts";
import CustomSelect from "../../../../components/Select/Select";

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload?.length) {
    const data = payload[0]?.payload;
    const color = data?.color || "#FFD700";
    return (
      <div style={{
        backgroundColor: "#fff",
        padding: "0",
        borderRadius: "6px",
        color: "#000",
        fontSize: "12px",
        fontWeight: "500",
        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
        fontFamily: "Helvetica, Arial, sans-serif"
      }}>
        <div style={{
          backgroundColor: "#EBEBEB",
          color: "#000",
          padding: "8px",
          margin: "0",
          display: "flex",
          alignItems: "center",
          gap: "5px"
        }}>
          {data?.name || "Unknown"}
          <span style={{
            display: "inline-block",
            width: "12px",
            height: "12px",
            borderRadius: "2px",
            backgroundColor: color,
            marginLeft: "5px"
          }}></span>
        </div>
        <div style={{
          backgroundColor: "#fff",
          padding: "8px",
          display: "flex",
          flexDirection: "column"
        }}>
          <div style={{ color: "#000", margin: "5px 0px" }}>
            <span style={{ fontWeight: "500" }}>Quantity: </span>
            <span style={{ fontWeight: "600" }}>{data?.qty || 0}</span>
            <span style={{ fontWeight: "500" }}> (Sub-units: <span style={{ fontWeight: "600" }}>{data?.subunits || 0}</span>)</span>
          </div>
          <div style={{ color: "#000", margin: "5px 0px" }}>
            <span style={{ fontWeight: "500" }}>Days of Cover: </span>
            <span style={{ fontWeight: "600" }}>{data?.daysOfCover || 0}</span>
          </div>
          <div style={{ color: "#000", margin: "5px 0px" }}>
            <span style={{ fontWeight: "500" }}>Category: </span>
            <span style={{ fontWeight: "600" }}>{data?.category || ''}</span>
          </div>
        </div>
      </div>
    );
  }
  return null;
};

// Custom tick component to ensure single line text
const CustomYAxisTick = (props) => {
  const { x, y, payload } = props;
  return (
    <g transform={`translate(${x},${y})`}>
      <text
        x={0}
        y={0}
        dy={4}
        textAnchor="end"
        fill="#2C2C49"
        fontSize="13"
        fontWeight="500"
        style={{
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        }}
      >
        {payload.value}
      </text>
    </g>
  );
};

const movementTypeList = [
  { name: 'Fast moving', value: 'fast-moving' },
  { name: 'Slow moving', value: 'slow-moving' }
]
const allObj = { name: 'All', value: 'all' }

const LowStockAlerts = () => {
  const dispatch = useDispatch();
  const { loading, error, lowStockAlerts } = useSelector(
    (state) => state.stockDashboard
  );
  const [lowStockData, setLowStockData] = useState([]);
  const [criticalCount, setCriticalCount] = useState(0);
  const [cautionCount, setCautionCount] = useState(0);
  const [filterValues, setFilterValues] = useState({ movementType: movementTypeList?.[0], category: allObj });
  const [categoryList, setCategoryList] = useState([allObj]);

  useEffect(() => {
    setCategoryList(categoryList);
  }, []);

  useEffect(() => {
    if (!lowStockAlerts || !Array.isArray(lowStockAlerts)) return;
    // Set category list for the filter dropdown
    const catList = lowStockAlerts?.map((li) => { return { name: li?.category, value: li?.category } })
    const uniqCatList = uniqBy(catList, 'value');
    setCategoryList([allObj, ...(uniqCatList ?? [])]);
  }, [lowStockAlerts]);

  useEffect(() => {
    if (!lowStockAlerts || !Array.isArray(lowStockAlerts)) return;

    // Filter by movement type
    let reqList = lowStockAlerts;
    if (filterValues?.movementType?.value && filterValues?.movementType?.value !== 'all') {
      reqList = lowStockAlerts?.filter((li) =>
        li?.movementType?.toLowerCase() === filterValues?.movementType?.value?.toLowerCase()
      );
    }

    // Filter by category
    let categoryBasedList = reqList;
    if (filterValues?.category?.value && filterValues?.category?.value !== 'all') {
      categoryBasedList = reqList?.filter((li) =>
        li?.category?.toLowerCase() === filterValues?.category?.value?.toLowerCase()
      );
    }

    // Format the filtered data for the chart
    const formatted = categoryBasedList?.map((item) => {
      const frontCategory = item?.qty <= 2 ? "Critical" : "Caution";
      const color = frontCategory === "Critical" ? "#FF3B30" : "#FFB200"; // Red & Yellow
      return {
        ...item,
        name: item?.productName,
        value: item?.subunits,
        count: item?.subunits,
        color,
        frontCategory
      };
    });

    // Update counts based on filtered data
    const critical = formatted?.filter(item => item?.frontCategory === "Critical")?.length || 0;
    const caution = formatted?.filter(item => item?.frontCategory === "Caution")?.length || 0;

    setLowStockData(formatted || []);
    setCriticalCount(critical);
    setCautionCount(caution);
  }, [filterValues, lowStockAlerts])

  if (loading && !lowStockData?.length)
    return <p>Loading low stock alerts...</p>;

  if (error) return <p>Error: {error}</p>;

  const onChangeFilter = (name, value) => {
    const reqObj = { ...filterValues, [name]: value };
    if (!value?.value && name === 'movementType') {
      reqObj.movementType = filterValues?.movementType;
    }
    if (!value?.value && name === 'category') {
      reqObj.category = allObj;
    }
    setFilterValues(reqObj);
  }

  return (
    <div style={{
      backgroundColor: "#fff",
      borderRadius: "12px",
      padding: "24px",
      boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
      // maxWidth: "1200px",
      margin: "auto",
      fontFamily: "'Inter', 'Segoe UI', sans-serif"
    }}>
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        marginBottom: "20px"
      }}>
        <div>
          <h3 style={{
            margin: "0 0 12px 0",
            fontSize: "16px",
            color: "#2C2C49",
            fontWeight: "600"
          }}>
            Low Stock Alerts
          </h3>
          <div style={{ display: "flex", alignItems: "center", gap: "20px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <div style={{
                width: "12px",
                height: "12px",
                borderRadius: "50%",
                backgroundColor: "#FF3B30",
                border: "2px solid #fff",
                boxShadow: "0 2px 4px rgba(255,107,107,0.3)"
              }} />
              <span style={{ fontSize: "13px", color: "#666", fontWeight: "500" }}>
                Critical ({criticalCount})
              </span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <div style={{
                width: "12px",
                height: "12px",
                borderRadius: "50%",
                backgroundColor: "#FFB200",
                border: "2px solid #fff",
                boxShadow: "0 2px 4px rgba(78,205,196,0.3)"
              }} />
              <span style={{ fontSize: "13px", color: "#666", fontWeight: "500" }}>
                Caution ({cautionCount})
              </span>
            </div>
            {/* <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <div style={{ 
                width: "12px", 
                height: "12px", 
                borderRadius: "2px", 
                background: "linear-gradient(45deg, #FF6B6B, #4ECDC4, #45B7D1, #96CEB4)",
                border: "2px solid #fff",
                boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
              }} />
              <span style={{ fontSize: "13px", color: "#666", fontWeight: "500" }}>
                Varied Colors
              </span>
            </div> */}
          </div>
        </div>
        <div className="flex-row-ali-cen">
          <div style={{ width: '150px' }}>
            <CustomSelect
              list={movementTypeList}
              value={filterValues?.movementType}
              handleChange={(e, value) =>
                onChangeFilter("movementType", value)
              }
              search
              label=""
            />
          </div>
          <div style={{ width: '150px' }} className="ml20">
            <CustomSelect
              list={categoryList}
              value={filterValues?.category}
              handleChange={(e, value) =>
                onChangeFilter("category", value)
              }
              search
              label=""
            />
          </div>
        </div>
        {/* <div style={{
          backgroundColor: "#F8F9FA",
          padding: "12px 20px",
          borderRadius: "8px",
          border: "2px solid #E9ECEF"
        }}>
          <span style={{
            fontSize: "14px",
            color: "#666",
            fontWeight: "500"
          }}>
            Total Items:
          </span>
          <span style={{
            fontSize: "18px",
            color: "#2C2C49",
            fontWeight: "700",
            marginLeft: "8px"
          }}>
            {total}
          </span>
        </div> */}
      </div>

      <div style={{
        backgroundColor: "#FAFBFC",
        borderRadius: "8px",
        padding: "16px",
        border: "1px solid #E9ECEF"
      }}>
        <BarChart
          width={1000}
          height={Math.max(250, lowStockData?.length * 45)}
          data={lowStockData}
          layout="vertical"
          margin={{ top: 15, right: 40, left: 220, bottom: 15 }}
        >
          <XAxis
            type="number"
            hide
            domain={[0, 'dataMax + 1']}
          />
          <YAxis
            type="category"
            dataKey="name"
            tick={<CustomYAxisTick />}
            width={200}
            interval={0}
            axisLine={false}
            tickLine={false}
          />
          <Tooltip
            content={<CustomTooltip />}
            cursor={{ fill: "rgba(0,0,0,0.02)" }}
          />
          <Bar
            dataKey="value"
            barSize={16}
            radius={[8, 8, 8, 8]}
          >
            {lowStockData?.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={entry?.color}
                style={{
                  filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.1))"
                }}
              />
            ))}
          </Bar>
        </BarChart>
      </div>
    </div>
  );
};

export default LowStockAlerts;